import { reactive } from "vue";
import messagingApi from "@/services/messagingApi";

/**
 * @typedef ComposeTarget
 * @property {string | number} recipientId
 * @property {string} product
 * @property {string | number} basicNeedsCaseId
 * @property {unknown | null} otherUser
 */

/**
 * @typedef InboxState
 * @property {boolean} isOpen
 * @property {"empty" | "thread" | "picker" | "compose"} rightPane
 * @property {string | null} activeConversationToken
 * @property {ComposeTarget | null} activeComposeTarget
 * @property {unknown[]} conversations
 * @property {unknown[]} eligibleTargets
 * @property {number} unreadTotal
 */

// Shared drawer state — InboxLauncher, InboxDrawer, and any of the legacy
// entry points (CaseHistory, StudentDetailView, etc.) all read/trigger the
// same instance, so a click anywhere in the app can drive the one drawer.
/** @type {InboxState} */
const state = reactive({
  isOpen: false,
  rightPane: "empty",
  activeConversationToken: null,
  activeComposeTarget: null,
  conversations: [],
  eligibleTargets: [],
  unreadTotal: 0
});

function close() {
  state.isOpen = false;
}

// The launcher's click handler — opens the drawer with nothing selected on
// the right. ConversationList loads the list itself when it mounts.
function open() {
  state.isOpen = true;
  state.rightPane = "empty";
}

// The badge total — every conversation, not just whatever page is loaded
// below. Safe to call any time (e.g. once per session from InboxLauncher),
// independent of the drawer ever having been opened.
async function loadUnreadTotal() {
  try {
    const { data } = await messagingApi.fetchUnreadCount();
    state.unreadTotal = data.unreadTotal;
    return data.unreadTotal;
  } catch {
    return state.unreadTotal;
  }
}

async function loadInbox(params = {}) {
  const { data } = await messagingApi.fetchInbox(params);
  // The list's own poll and the open thread's poll run on independent
  // timers, so the list's can win the race and show unreadCount > 0 for
  // the conversation you're actively looking at - the server hasn't
  // recorded it as read yet since that only happens once the thread's own
  // poll gets around to its real fetch. Correct it here, once, so every
  // consumer (rows, the "Unread" filter) sees a consistent value rather
  // than patching each read site separately.
  state.conversations = data.conversations.map(conversation =>
    conversation.token === state.activeConversationToken
      ? { ...conversation, unreadCount: 0 }
      : conversation
  );
  return data;
}

// Opening a thread doesn't change any conversation's last_message_id, so
// the list's peek-based poll has no way to ever notice "this got read" on
// its own - it's only wired to detect new messages. Clear it locally the
// instant a thread opens, rather than waiting on a poll that will never
// fire for this reason.
function clearUnreadLocally(conversationToken) {
  const conversation = state.conversations.find(
    c => c.token === conversationToken
  );
  if (!conversation || conversation.unreadCount === 0) return;

  state.unreadTotal = Math.max(0, state.unreadTotal - conversation.unreadCount);
  conversation.unreadCount = 0;
}

function matchTarget(targets, { recipientId, product, basicNeedsCaseId }) {
  return targets.find(
    target =>
      target.otherUser.id === recipientId &&
      target.product === product &&
      (target.basicNeedsCase?.id ?? null) === (basicNeedsCaseId ?? null)
  );
}

// The one entry point every trigger (launcher row, or any of the 6 legacy
// entry points) calls. A token opens the thread directly; a known
// recipient/product/case with no token yet resolves through
// eligible_targets to decide thread vs. compose — callers never need to
// know which case they're in.
async function openConversation({
  conversationToken,
  recipientId,
  product,
  basicNeedsCaseId
} = {}) {
  state.isOpen = true;

  if (conversationToken) {
    state.rightPane = "thread";
    state.activeConversationToken = conversationToken;
    state.activeComposeTarget = null;
    clearUnreadLocally(conversationToken);
    return;
  }

  const { data } = await messagingApi.fetchEligibleTargets();
  const match = matchTarget(data.targets, {
    recipientId,
    product,
    basicNeedsCaseId
  });

  if (match?.existingConversationToken) {
    state.rightPane = "thread";
    state.activeConversationToken = match.existingConversationToken;
    state.activeComposeTarget = null;
    clearUnreadLocally(match.existingConversationToken);
  } else {
    state.rightPane = "compose";
    state.activeConversationToken = null;
    state.activeComposeTarget = {
      recipientId,
      product,
      basicNeedsCaseId,
      otherUser: match?.otherUser ?? null
    };
  }
}

// Checking eligible targets to legacy entry points
async function loadEligibleTargets() {
  const { data } = await messagingApi.fetchEligibleTargets();
  state.eligibleTargets = data.targets;
  return state.eligibleTargets;
}

// Returns a boolean for gating at legacy entry points
function isEligibleTarget({ recipientId, product, basicNeedsCaseId } = {}) {
  return !!matchTarget(state.eligibleTargets, {
    recipientId,
    product,
    basicNeedsCaseId
  });
}

async function openPicker() {
  state.isOpen = true;
  state.rightPane = "picker";
  await loadEligibleTargets();
}

export default function useInboxState() {
  return {
    state,
    open,
    close,
    loadUnreadTotal,
    loadInbox,
    loadEligibleTargets,
    isEligibleTarget,
    openConversation,
    openPicker
  };
}
